import type { PhaseFourDto, PhaseHelpDto, PhaseProcessDto } from '@bk/contracts';
import { useState } from 'react';
import { useTenant } from '../../lib/useTenant';
import { Figures, Registry, Scrap, talkAbout } from './Fase';
import { PhaseFrame, usePhase } from './fasi-comune';
import type { PhasePageProps } from './fasi-pagine';

const SCRAP_INTRO = 'Per ogni processo dell’azienda al suo meglio: se le fonti lo mostrano oggi, se lo fa qualcun altro o se manca. Lo scarto dice cosa le fonti mostrano o non mostrano oggi, non se fate bene o male.';

const STATE: Record<string, string> = { aperta: 'Aperta', scelta_timonio: 'Scelta di Timonio', non_chiesta: 'Non chiesta' };

/** «Cose che aiuterebbero qui» (art. 7-bis.13): voci del registro di fasi già fatte, con il collegamento. */
export function Helps({ list }: { list: PhaseHelpDto[] }) {
  const [reg, setReg] = useState(false);
  if (!list.length) return null;
  return (
    <section className="sn-panel" style={{ gap: 10, padding: '20px 24px' }}>
      <h2 className="sn-h2">Cose che aiuterebbero qui</h2>
      <p className="sn-note" style={{ fontSize: 14 }}>
        Non sono domande nuove: sono voci del registro di fasi già fatte. Se rispondi, questa fase si aggiorna da sola, senza rifarla.
      </p>
      <table className="sn-tbl wide">
        <tbody>
          {list.map((h) => (
            <tr key={h.id}>
              <td>
                <b>{h.voce}</b>
                <span className="sub">{h.domanda}</span>
              </td>
              <td className="v mute" style={{ fontFamily: 'var(--sn-mono)', width: 70 }}>
                fase {h.fase}
              </td>
              <td style={{ width: 150 }}>
                <span className="sn-tag gold">{STATE[h.stato] ?? h.stato}</span>
              </td>
              <td className="f">
                {h.cambia}{' '}
                <button type="button" className="sn-link" onClick={() => setReg(true)}>
                  Rispondi
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {reg && <Registry onClose={() => setReg(false)} />}
    </section>
  );
}

const gradeTag = (g: string) => <span className={`sn-tag ${g === 'certo' ? 'ok' : 'gold'}`}>{g}</span>;

/** Un processo primario: le fasi del processo con cosa entra, cosa esce e dove (art. 3.98). */
function Primary({ p }: { p: PhaseProcessDto }) {
  const [why, setWhy] = useState(false);
  const { isManager } = useTenant();
  return (
    <article className="sn-site">
      <div className="sn-ph">
        <h2 className="sn-h2">{p.nome}</h2>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {gradeTag(p.grado)}
          <span className="sn-tag ok">Primario</span>
          {p.etichetta && <span className="sn-tag">{p.etichetta}</span>}
        </span>
      </div>
      <p className="line">
        {p.attivita && <>Attività: {p.attivita} · </>}
        {p.per_chi && <>per {p.per_chi} · </>}
        dove si svolge: {p.dove} <span className="sn-src">· {p.fonti.join(' · ')}</span>
      </p>
      <table className="sn-tbl">
        <thead>
          <tr>
            <th>Fase del processo</th>
            <th>Cosa entra</th>
            <th>Cosa esce</th>
            <th>Dove</th>
          </tr>
        </thead>
        <tbody>
          {p.fasi_del_processo.map((f) => (
            <tr key={f.n}>
              <td className="g">
                {f.n}. {f.nome}
              </td>
              <td className="v">{f.entra}</td>
              <td className="v">{f.esce}</td>
              <td className="f">{f.dove}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {p.adattato && <p className="sn-note">{p.adattato}</p>}
      {p.nota && <p className="sn-note">La tua risposta: {p.nota}</p>}
      <div className="sn-pills">
        <span className="sn-tag gold">Volume: {p.volume.toLowerCase()}</span>
        <span className="sn-tag">Responsabile: {p.responsabile.toLowerCase()}</span>
        <span className="sn-tag">Obblighi che realizza: arrivano dalla fase 6</span>
      </div>
      <div className="acts">
        <button type="button" className="sn-link" style={{ fontSize: 14, fontWeight: 600 }} onClick={() => setWhy(!why)}>
          Perché? Le fonti di questo processo
        </button>
        {isManager && (
          <>
            <button type="button" className="sn-link" style={{ color: '#c3cbe0' }} onClick={() => talkAbout(`Sul processo «${p.nome}»: `)}>
              Correggi
            </button>
            <button type="button" className="sn-link" style={{ color: '#c3cbe0' }} onClick={() => talkAbout(`Il processo «${p.nome}» da noi non c’è: `)}>
              Togli
            </button>
          </>
        )}
      </div>
      {why && (
        <p className="sn-why" style={{ fontSize: 13 }}>
          <span className="q">Le fonti di questo processo.</span> {p.descrizione} Fonti: {p.fonti.join(', ')}. Grado della conclusione: {p.grado}.
        </p>
      )}
    </article>
  );
}

/** I processi di supporto: interni o affidati e a chi (art. 3.99). */
function Support({ list, classe }: { list: PhaseProcessDto[]; classe: string | null }) {
  const [open, setOpen] = useState<string | null>(null);
  const { isManager } = useTenant();
  if (!list.length) return null;
  return (
    <section className="sn-panel flush" style={{ gap: 0 }}>
      <div className="sn-ph" style={{ padding: '20px 24px 6px' }}>
        <h2 className="sn-h2">I processi di supporto</h2>
      </div>
      <p className="sn-note" style={{ padding: '0 24px 12px', fontSize: 14 }}>
        Interni o affidati, decisi con la classe dimensionale{classe ? ` (${classe})` : ''} e le fonti. Ognuno ha le fasi del processo, il volume da confermare e il responsabile da assegnare con il tipo di ruolo, come i primari.
      </p>
      <table className="sn-tbl wide">
        <thead>
          <tr>
            <th>Processo</th>
            <th style={{ width: 230 }}>Interno o affidato</th>
            <th style={{ width: 170 }}>A chi</th>
            <th style={{ width: 190 }}>Fonte</th>
            <th style={{ width: 110 }}>Grado</th>
          </tr>
        </thead>
        <tbody>
          {list.map((p) => (
            <tr key={p.id} onClick={() => setOpen(open === p.id ? null : p.id)} style={{ cursor: 'pointer' }}>
              <td>
                <b>{p.nome}</b>
                {open === p.id && (
                  <span className="sub">
                    {p.fasi_del_processo.map((f) => `${f.n}. ${f.nome} (${f.entra} → ${f.esce}, ${f.dove})`).join(' · ')}. Responsabile: {p.responsabile.toLowerCase()}. Volume: {p.volume.toLowerCase()}.
                  </span>
                )}
                {p.nota && <span className="sub">La tua risposta: {p.nota}</span>}
              </td>
              <td>
                {p.da_confermare ? <span className="sn-tag gold">Da confermare</span> : <span className={`sn-tag ${p.gestione === 'interno' ? 'ok' : ''}`}>{p.gestione === 'affidato' ? 'Affidato' : 'Interno'}</span>}
                {(p.dettaglio || p.da_confermare) && <span className="sub">{p.da_confermare ? `${p.gestione ?? ''}${p.dettaglio ? `, ${p.dettaglio}` : ''}` : p.dettaglio}</span>}
              </td>
              <td className="v">{p.affidato_a ?? '—'}</td>
              <td className="f on">{p.fonti.join(' · ')}</td>
              <td>{gradeTag(p.grado)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="acts" style={{ padding: '12px 24px 18px', display: 'flex', gap: 18 }}>
        <span className="sn-note">Tocca un processo per le sue fasi del processo.</span>
        {isManager && (
          <button type="button" className="sn-link" style={{ fontSize: 14 }} onClick={() => talkAbout('Manca questo processo: ')}>
            Aggiungi un processo che manca
          </button>
        )}
      </div>
    </section>
  );
}

/** Il modello di business ISA di ogni attività (art. 3.162): del settore, con il perché; mai il punteggio. */
function IsaModels({ list }: { list: NonNullable<PhaseFourDto['modello_isa']> }) {
  if (!list.length) return null;
  return (
    <section className="sn-panel flush" style={{ gap: 0 }}>
      <div className="sn-ph" style={{ padding: '20px 24px 6px' }}>
        <h2 className="sn-h2">Il modello di business ISA</h2>
      </div>
      <p className="sn-note" style={{ padding: '0 24px 12px', fontSize: 14 }}>
        Il modello del settore dai decreti del Ministero dell’economia: i processi primari lo seguono. Timonio non ha il tuo modello ISA né il tuo punteggio e non li stima.
      </p>
      <table className="sn-tbl wide">
        <thead>
          <tr>
            <th style={{ width: 230 }}>Attività</th>
            <th>Modello</th>
            <th style={{ width: 230 }}>Fonte</th>
          </tr>
        </thead>
        <tbody>
          {list.map((m) => (
            <tr key={m.attivita}>
              <td className="v">{m.attivita}</td>
              <td>
                <b>{m.testo}</b>
                <span className="sub">{m.perche}</span>
              </td>
              <td className="f on">{m.fonte ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * La fase 4 «I processi» (docs/mockup/percorso-sinapsi/07-fase4; contratto n. 1 versione 2.4, art. 8, fase 4; ADR 0176):
 * come l'azienda crea valore, dalla richiesta all'incasso, e cosa lo sostiene.
 */
export function Fase4Page({ next, onNext, sealing, stopped = false }: PhasePageProps) {
  const { q, reload, confirm, redo } = usePhase<PhaseFourDto>(4);
  return (
    <PhaseFrame
      // Lo scarto va prima delle domande, come nella pagina di prova: lo mostra `beforeQuestions`, non la cornice.
      d={q.data && { ...q.data, scarto: [] }}
      error={q.error}
      eyebrow="Fase 4 · I processi"
      title={(d) => `Come ${d.azienda ?? 'la tua azienda'} lavora, dalla richiesta all’incasso`}
      working="Preparo i processi"
      questionsIntro="Solo fatti che cambiano un processo. Il volume non te lo chiedo ora: serve solo ai calcoli della fase 11."
      questionsEmpty="Nessuna domanda per te in questa fase: le fonti e la classe dimensionale bastano a decidere i processi."
      scrapIntro={SCRAP_INTRO}
      next={next}
      onNext={onNext}
      sealing={sealing}
      stopped={stopped}
      onConfirm={confirm}
      onRedo={redo}
      reload={reload}
      beforeQuestions={(d) => (
        <>
          <Scrap list={q.data?.scarto ?? []} intro={SCRAP_INTRO} />
          <Helps list={d.aiuterebbero} />
        </>
      )}
    >
      {(d) => (
        <>
          <Figures list={d.numeri} />
          {d.primari.map((p) => (
            <Primary key={p.id} p={p} />
          ))}
          <Support list={d.supporto} classe={d.classe} />
          <IsaModels list={d.modello_isa ?? []} />
        </>
      )}
    </PhaseFrame>
  );
}
